import {
  PersonEntity,
  personsSelector,
  personType,
  SiteEntity,
  TrailerEntity,
  trailersSelector,
  useGetAllPersonsQuery,
  useGetAllSitesQuery,
  useGetAllTrailersQuery,
  useGetAllVehiclesQuery,
  VehicleEntity,
  vehiclesSelector
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {MapFilter} from "../components/map/filter/map-filter";
import {useMemo} from "react";
import {mapSiteSelector} from "../store/ui/map-page/selectors";

export type UseFleetMapDataProps = {
  regionId?: string
  mapFilter?: MapFilter
}

export type UseFleetMapDataResult = {
  data: {
    sites: SiteEntity[]
    persons: PersonEntity[]
    trailers: TrailerEntity[]
    vehicles: VehicleEntity[]
  }
  isLoading: boolean,
  isFetching: boolean
}

export const useFleetMapData = (props: UseFleetMapDataProps): UseFleetMapDataResult => {
  const {
    regionId,
    mapFilter,
  } = props;

  const selectPersons = useMemo(() => {
    return personsSelector();
  }, []);

  // TODO: check difference for operators
  const {persons, isPersonsLoading, isPersonsFetching} = useGetAllPersonsQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      persons: selectPersons(result, {regionId: regionId, personType: personType.driver}),
      isPersonsLoading: result.isLoading,
      isPersonsFetching: result.isFetching,
    })
  });

  const selectTrailers = useMemo(() => {
    return trailersSelector();
  }, []);

  const {trailers, isTrailersLoading, isTrailersFetching} = useGetAllTrailersQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      trailers: selectTrailers(result, {regionId: regionId}),
      isTrailersLoading: result.isLoading,
      isTrailersFetching: result.isFetching,
    })
  });

  const selectVehicles = useMemo(() => {
    return vehiclesSelector();
  }, []);

  const {vehicles, isVehiclesLoading, isVehiclesFetching} = useGetAllVehiclesQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      vehicles: selectVehicles(result, {regionId: regionId}),
      isVehiclesLoading: result.isLoading,
      isVehiclesFetching: result.isFetching,
    })
  });

  const selectSites = useMemo(() => {
    return mapSiteSelector();
  }, []);

  const {sites, isSitesLoading, isSitesFetching} = useGetAllSitesQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      sites: selectSites(result, mapFilter),
      isSitesLoading: result.isLoading,
      isSitesFetching: result.isFetching,
    })
  });

  const isLoading = useMemo(() => {
    return isTrailersLoading || isSitesLoading || isPersonsLoading || isVehiclesLoading;
  }, [isTrailersLoading, isSitesLoading, isPersonsLoading, isVehiclesLoading]);

  const isFetching = useMemo(() => {
    return isTrailersFetching || isSitesFetching || isPersonsFetching || isVehiclesFetching;
  }, [isTrailersFetching, isSitesFetching, isPersonsFetching, isVehiclesFetching]);

  return {
    data: {
      sites: sites,
      persons: persons,
      trailers: trailers,
      vehicles: vehicles,
    },
    isLoading: isLoading,
    isFetching: isFetching,
  };
};
